import React, { createRef, useEffect, useRef, useState } from 'react';
import "./chat.scss"
import { API_URL } from '../../config/constants';
import HttpService from "../../config/http.service";
import { Toast } from '../../services';
import { Iconsvg, InputField, FileUploadItem, FormFieldErrorInfo, ButtonField } from '@wk/components-react16';
import robot from "../../assets/images/robot-solid.svg"
import logo from "../../assets/images/wheel-small.svg"
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import Markdown from 'react-markdown';
import PromptStudio from '../prompt/model/PromptStudio';
import { hideLoading, showLoading } from '../../store/authReducer';

const Chat = () => {
    const { authUser, authProject } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch();
    const divRef = useRef<HTMLDivElement>(null);
    const promptObj = {
        id: null,
        name: '',
        text: '',
        promptType: null,
        jsonOutput: false,
        score: 0,
        categoryScore: null,
        tags: []
    };
    const [prompt, setPrompt] = useState(promptObj);
    const [promptTypeList, setPromptTypeList] = useState([] as any);
    const [isOpenPromptStudio, setIsOpenPromptStudio] = useState(false);
    const [isLoad, setIsLoad] = useState(false);
    const [userDefinedPromptType, setUserDefinedPromptType] = useState(false);
    const [isOpenImproveScore, setIsOpenImproveScore] = useState(false);

    const [fileItems , setFileItems] = useState([] as any);
    const [chatHistory, setChatHistory] = useState<{
        chat_history: { role: string; content: string, file_name: string, is_pdf: boolean }[];
    }>({
        chat_history: []
    });
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [isCopy, setIsCopy] = useState(false);

    const placeholder: string = 'Enter your text here...';
    const preChatText: any = {
        text_one: 'Act as a prompt engineer. Write a complete prompt for "Summarize any news article and also give me a catchy headline for internal newsletters."',
        text_two: 'Act as a prompt engineer. Write a complete prompt for "Can you classify any list of customer reviews into positive, negative, or neutral?"',
        text_three: 'Act as a prompt engineer. Write a complete prompt for "Can you extract all legally binding requirements from this compliance document?"'
    }

    const removeFile = (indexToRemove: any) => {
        setFileItems(fileItems.filter((_: File, index: number) => index !== indexToRemove));
    };

    const handleFileInput = (files: any) => {
        setFileItems([...fileItems, ...Array.from(files as FileList)]);
    };

    const scrollToButtom = () => {
        setTimeout(() => {
            const scroll = document.getElementById("scrollview");
            scroll!.scrollTo({
                top: scroll!.scrollHeight,
                behavior: "smooth",
            });
        }, 500);
    }

    const handleContentChange = (e: React.FormEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        // Remove <br> if the content is effectively empty (only whitespace or <br>)
        if (el.innerHTML === '<br>' || el.textContent?.trim() === '' || el.innerText?.trim() === '\n') {
            el.innerHTML = '';
            setMessage('');
        }
        setMessage(el.textContent?.trim() || '');
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // prevent newline
            handleSubmit(e, message);
        }
        // Shift+Enter will allow a newline naturally
    };

    const handleSubmit = async (_e: any, search: string) => {
        _e.preventDefault();
        if (search.trim() === "") return;
        let chatObj: any = {
            role: "user",
            content: search,
            file_name: '',
            is_pdf: false
        }
        const formData = new FormData();
        if (fileItems.length > 0) {
            for (let file of fileItems) {
                /* chatObj.file_name = file.name;
                chatObj.is_pdf = true; */
                formData.append('pdfFile', file);
            }
        }
        chatHistory?.chat_history?.push(chatObj);
        setChatHistory({ ...chatHistory });
        scrollToButtom();
        formData.append('requestChatHistory', JSON.stringify(chatHistory));
        setMessage('');
        if (divRef.current) {
            divRef.current.innerText = '';
            divRef.current.innerHTML = '';
        }
        setLoading(true);
        setIsSubmit(true);
        setFileItems([]);

        HttpService.postRequestFormData(API_URL.CHAT_GENERATED_PROMPT, formData).then(
            (res: any) => {
                if (res.data) {
                    setChatHistory(res.data);
                }
                scrollToButtom();
                setLoading(false);
                setIsSubmit(false);
            },
            (err: any) => {
                Toast(err.message, "error");
                setLoading(false);
                setIsSubmit(false);
            },
        )
    }

    const handleGeneratePrompt = (): void => {
        dispatch(showLoading());
        if (chatHistory?.chat_history?.length === 0) {
            Toast('Please enter a message to generate a prompt.', "error");
            setLoading(false);
            setIsSubmit(false);
            dispatch(hideLoading());
            return;
        }
        HttpService.postRequest(API_URL.GET_CHAT_GENERATED_PROMPT, chatHistory).then(
            (res: any) => {
                if (res.data) {
                    let promptType: any = null;
                    if (res.data?.prompt_type) {
                        promptType = promptTypeList.find((item: any) => item.code.toUpperCase() === res.data.prompt_type.toUpperCase());
                    }
                    setPrompt({
                        ...prompt,
                        name: res.data?.prompt_name || '',
                        text: res.data?.prompt || '',
                        promptType: promptType || null,
                        jsonOutput: prompt.jsonOutput,
                        score: prompt.score,
                        categoryScore: prompt.categoryScore,
                        tags: prompt.tags,
                    });
                    setIsOpenPromptStudio(true);
                }
                scrollToButtom();
                setLoading(false);
                setIsSubmit(false);
                dispatch(hideLoading());
            },
            (err: any) => {
                dispatch(hideLoading());
                Toast(err.message, "error");
                setLoading(false);
                setIsSubmit(false);
            },
        )

    }

    const extractFirstName = (email: any) => {
        const username = email.split('@')[0];
        const firstName = username.split('.')[0];
        return firstName;
    };

    const handleCopy = async (text: any) => {
        try {
            setIsCopy(true);
            await navigator.clipboard.writeText(text);
            setTimeout(() => {
                setIsCopy(false);
            }, 500);
        } catch (err) {
            setIsCopy(false);
            Toast('Failed to copy text.', "error");
        }
    };

    const getPromptType = () => {
        HttpService.getRequest(`${API_URL.GET_PROMPT_TYPE_BY_PROJECT_ID_APPLICABLE}/${authProject.id}`).then(
            (res: any) => {
                setPromptTypeList(res);
            },
            (err: any) => {
                Toast(err.message, "error");
            },
        )
    }

    useEffect(() => {
        const extractedFirstName = extractFirstName(authUser?.username);
        setFirstName(extractedFirstName);
    }, []);

    useEffect(() => {
        if (authProject && authProject.id) {
            getPromptType();
        }
    }, [authProject]);

    return (
        <section className="flex wk-card-container-v-dividers">
            <div className="wk-card-container-body-cell p-20 pr-0">
                <div className="custom-chat-container position-relative">
                    <div className="chat-container scroll" id="scrollview">
                        <div className='chat-content'>
                            {chatHistory?.chat_history?.length > 0 ? chatHistory.chat_history.map((chat: any, i: number) => {
                                if (!chat?.is_pdf) {
                                    return (
                                        <div className='clearfix position-relative' key={i}>
                                            <div className={`chat${chat?.role == 'user' ? ' right' : ''}`}>
                                                <span className="chat-avatar">
                                                    {chat?.role == 'assistant' ? <img style={{ width: "20px" }} src={logo} alt="" />
                                                        : <span className='icon'>
                                                            <i className="wk-icon-filled-user"></i>
                                                        </span>
                                                    }
                                                </span>
                                                <div className="chat-msg">
                                                    {chat?.role == 'user' && chat?.file_name && (
                                                        <span className='icon file-icon' title={chat.file_name}>
                                                            <i className="wk-icon-attach"></i>
                                                        </span>
                                                    )}  
                                                    <pre className="mb-0">
                                                        {chat?.role == 'assistant' ? <Markdown>{chat.content}</Markdown>
                                                            : <span>{chat.content}</span>}
                                                    </pre>
                                                    {chat?.role == 'assistant' ?
                                                        <div className="mt-10 cursor-pointer" onClick={(event: any) => handleCopy(chat.content)}>
                                                            <Iconsvg name={isCopy ? "check" : "files"} color='#9A67F3'></Iconsvg>
                                                        </div>
                                                        : null}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                            }) : <div className='pl-10 pr-10'>
                                    <h1 className='wk-weight-400 wk-primary-blue-shade1'>Hello, {firstName}<br />
                                        <span className='wk-primary-gray-tint1'>How can I help you today?</span>
                                    </h1>
                                    <div className='wk-row mt-30 mb-20'>
                                        <div className="wk-col-12">
                                            <div className='text-center pt-10 pb-10'><Iconsvg name="dialogue" size={20}></Iconsvg></div>
                                            <h5 className='text-center'>Act as a prompt engineer. Write a complete prompt for</h5>
                                        </div>
                                    </div>
                                    <div className='wk-row'>
                                        <div className="wk-col-6">
                                            <div onClick={(e) => handleSubmit(e, preChatText.text_one)}  className="y-card p-10 rounded-3 mb-15 cursor-pointer">
                                                Summarize any news article and also give me a catchy headline for internal newsletters.
                                            </div>
                                        </div>
                                        <div className="wk-col-6 pl-20 pr-20">
                                            <div onClick={(e) => handleSubmit(e, preChatText.text_two)} className="y-card p-10 rounded-3 mb-15 cursor-pointer">
                                                Can you classify any list of customer reviews into positive, negative, or neutral?
                                            </div>
                                        </div>
                                        {/* <div className="wk-col-4">
                                            <div onClick={(e) => handleSubmit(e, preChatText.text_three)} className="y-card p-10 rounded-3 mb-15 cursor-pointer">
                                                Can you extract all legally binding requirements from this compliance document?
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                            }
                        </div>
                    </div>

                    <div className='chat-input rounded-4'>
                        <form className='rounded-4' onSubmit={(e: any) => handleSubmit(e, message)}>
                            {loading && (<div className="spinner-wrapper" >
                                <div className="spinner-dot"></div>
                            </div>)}
                            <div className='space'>
                                <div
                                    ref={divRef}
                                    contentEditable="true"
                                    onInput={handleContentChange}
                                    onKeyDown={handleKeyDown}
                                    suppressContentEditableWarning
                                    style={{
                                        minHeight: '32px',
                                    }}
                                    id="contentEditable"
                                    className={`content-editable ${(message.trim() === '' || message === '\n') ? 'place-holder' : ''}`}
                                    data-placeholder={placeholder}
                                >
                                </div>
                                <button className="chat-input-inlay" aria-label="Submit" type="submit" disabled={isSubmit}>
                                    <i aria-hidden="true" className="wk-icon-filled-send"></i>
                                </button>
                            </div>
                            <div className="mt-2">
                                <div className="d-flex flex-wrap">
                                    <div>
                                        <div className="d-flex flex-row align-items-center">
                                            <label className="p-2 rounded cursor-pointer" htmlFor="attachment">
                                                <i aria-hidden="true" className="wk-icon-filled-attach"></i>
                                            </label>

                                            <input
                                                type="file"
                                                id="attachment"
                                                hidden
                                                accept=".pdf,application/pdf"
                                                onChange={(e) => handleFileInput(e.target.files)}
                                            />
                                            {fileItems.map((item: File, i: number) => (
                                                <span key={i} className="wk-size-1 bg-light px-2 py-1 mr-1 mt-1 rounded d-inline-block">
                                                    {item.name}
                                                    <span
                                                        className="fs-lg fw-bold ml-2 px-2 text-danger rounded cursor-pointer"
                                                        onClick={() => removeFile(i)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        x
                                                    </span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className='ms-auto'>
                                        <ButtonField iconPosition={'right'} mode={'ghost'} size='small'>
                                            <button type="button" disabled={chatHistory?.chat_history?.length === 0}
                                                onClick={() => handleGeneratePrompt()} className='mr-10'>
                                                Generate Prompt
                                            </button>
                                        </ButtonField>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {isOpenPromptStudio && <PromptStudio data={{ isOpenPromptStudio, setIsOpenPromptStudio, prompt, setPrompt, setIsLoad, promptObj, userDefinedPromptType, setUserDefinedPromptType, isOpenImproveScore, setIsOpenImproveScore }} />}
        </section>
    );
};

export default Chat;